import { Application } from "pixi.js";

/** The renderer could not initialise (no WebGL, a lost GPU process): the boot boundary shows the browser copy for it. */
export class RendererUnavailableError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "RendererUnavailableError";
  }
}
import { ViewportStore } from "@slot/presentation";

export interface SlotAppConfig {
  /** Element the game takes over. It is made `position: relative` and filled. */
  readonly host: HTMLElement;
  /** Canvas clear colour. */
  readonly background: number;
  /**
   * A transparent canvas over a DOM background layer (decision 67): the canvas clears to nothing and `backgroundRoot`, an
   * element under it, shows through wherever the stage draws nothing. Absent: an opaque canvas and no background root.
   */
  readonly transparent?: boolean;
  /** Renderer preference. WebGL is the verified path (headless e2e runs on it too). */
  readonly preference?: "webgl" | "webgpu";
  /** Device pixel ratio cap; defaults to min(devicePixelRatio, 2). */
  readonly maxResolution?: number;
}

export interface SlotApp {
  readonly app: Application;
  /** The DOM layer under the canvas, filled, for a scene background drawn outside the canvas (decision 67); null on an opaque canvas. */
  readonly backgroundRoot: HTMLDivElement | null;
  /** Holds the canvas. Sized by the host; observed for resizes. */
  readonly canvasHost: HTMLDivElement;
  /**
   * Shell-owned DOM layer above the canvas (info page, dev tools, vConsole).
   * `pointer-events: none` by default — children opt back in.
   */
  readonly domOverlayRoot: HTMLDivElement;
  readonly viewport: ViewportStore;
  destroy(): void;
}

function fill(element: HTMLElement): void {
  element.style.position = "absolute";
  element.style.inset = "0";
  element.style.overflow = "hidden";
}

/**
 * Boots one Pixi Application into `host`: canvas host div + DOM overlay root (and, for a transparent canvas, a background
 * root under both), a ResizeObserver that resizes the renderer and feeds the viewport store.
 * No scenes: boot, game and overlays are layers on the one stage.
 */
export async function createSlotApp(config: SlotAppConfig): Promise<SlotApp> {
  const { host } = config;
  if (getComputedStyle(host).position === "static") {
    host.style.position = "relative";
  }

  const backgroundRoot = config.transparent ? document.createElement("div") : null;
  if (backgroundRoot) {
    backgroundRoot.className = "slot-background-root";
    fill(backgroundRoot);
    backgroundRoot.style.pointerEvents = "none";
  }

  const canvasHost = document.createElement("div");
  canvasHost.className = "slot-canvas-host";
  fill(canvasHost);

  const domOverlayRoot = document.createElement("div");
  domOverlayRoot.className = "slot-dom-overlay";
  fill(domOverlayRoot);
  domOverlayRoot.style.pointerEvents = "none";

  host.append(...(backgroundRoot ? [backgroundRoot] : []), canvasHost, domOverlayRoot);

  const resolution = Math.min(window.devicePixelRatio || 1, config.maxResolution ?? 2);
  const app = new Application();
  try {
    await app.init({
      background: config.background,
      // Only a transparent canvas changes the context (an alpha one, at a compositing cost): an opaque one inits as before.
      ...(config.transparent ? { backgroundAlpha: 0 } : {}),
      preference: config.preference ?? "webgl",
      resolution,
      autoDensity: true,
      antialias: false,
      width: Math.max(1, canvasHost.clientWidth),
      height: Math.max(1, canvasHost.clientHeight),
    });
  } catch (error) {
    throw new RendererUnavailableError(`createSlotApp: renderer init failed: ${error instanceof Error ? error.message : String(error)}`);
  }
  canvasHost.appendChild(app.canvas);

  const viewport = new ViewportStore();
  const applySize = (width: number, height: number): void => {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    app.renderer.resize(w, h);
    viewport.setViewportSize(w, h);
  };
  applySize(canvasHost.clientWidth, canvasHost.clientHeight);

  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (!entry) return;
    applySize(entry.contentRect.width, entry.contentRect.height);
  });
  observer.observe(canvasHost);

  return {
    app,
    backgroundRoot,
    canvasHost,
    domOverlayRoot,
    viewport,
    destroy(): void {
      observer.disconnect();
      app.destroy(true, { children: true });
      backgroundRoot?.remove();
      canvasHost.remove();
      domOverlayRoot.remove();
    },
  };
}
